import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button, Card, Input, Alert, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = { name: '', description: '', is_active: true, slug: '' };

export default function CompanyDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [row, setRow] = useState(isNew ? { ...BLANK } : null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    fetchApi('crud', { model: 'AppCompany', action: 'show', where: { id } })
      .then((res) => setRow(res.data || res))
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, value) => setRow((r) => ({ ...r, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      if (isNew) {
        const res = await fetchApi('crud', { model: 'AppCompany', action: 'create', data: row });
        nav(`/app-company/${res.data.id || res.id}`, { replace: true });
      } else {
        await fetchApi('crud', { model: 'AppCompany', action: 'update', where: { id }, data: row });
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete company "${row.name}"? This cannot be undone.`)) return;
    try {
      await fetchApi('crud', { model: 'AppCompany', action: 'delete', where: { id } });
      nav('/app-company');
    } catch (ex) {
      setErr(apiError(ex));
    }
  };

  if (loading) return <PageShell><Loader text="Loading company…" /></PageShell>;
  if (!row) return <PageShell><Alert variant="error" title="Could not load">{err || 'Company not found'}</Alert></PageShell>;

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-company"
        backLabel="Companies"
        title={isNew ? 'New Company' : row.name}
        actions={(
          <Can module="companies" action="govern">
            <div className="flex items-center gap-2">
              {!isNew && (
                <>
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={!!row.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
                  </label>
                  <Button size="sm" variant="secondary" onClick={remove}><Trash2 size={14} className="mr-1 inline" /> Delete</Button>
                </>
              )}
              <Button size="sm" onClick={save} disabled={saving || !row.name}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <Card className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name" value={row.name || ''} onChange={(e) => setField('name', e.target.value)} />
          <Input label="Description" value={row.description || ''} onChange={(e) => setField('description', e.target.value)} />
          <Input label="Slug" value={row.slug || ''} disabled={!isNew} onChange={(e) => setField('slug', e.target.value)} />
        </div>
      </Card>
    </PageShell>
  );
}
